import { supertypeClass, property } from '@haventech/amorphic';
import { Persistent } from '@hit/amorphic-types';
import { AuditEntity, AuditEntityParam } from './AuditEntity';
import { DbTransaction } from '@hit/utils';
import { Customer } from '../customer/Customer';
import { Agent } from '../customer/Agent';
import { AgentProxy } from '../agent/AgentProxy';

export type DbChanges = {
  [entityName: string]: Array<AuditEntityParam>;
};

export type ChangeLog = {
  _id: string;
  entityId: string;
  attributeName: string;
  oldValue: string;
  newValue: string;
};

@supertypeClass({ toClient: false, toServer: false })
export class AuditEvent extends Persistent {
  static new(
    functionName: string,
    agentOrAgentFromProxy: Agent,
    proxy: AgentProxy,
    customer: Customer,
    changes: DbChanges
  ): AuditEvent {
    return new AuditEvent().init(functionName, agentOrAgentFromProxy, proxy, customer, changes);
  }

  private init(
    functionName: string,
    agentOrAgentFromProxy: Agent,
    proxy: AgentProxy,
    customer: Customer,
    changes: DbChanges
  ): this {
    this.functionName = functionName;
    this.applicationId = customer && customer.application ? customer.application._id : null;
    this.customerId = customer && customer._id;

    if (customer.isCoverpathCustomer()) {
      this.userId = proxy ? proxy._id : null;
      this.agentId = agentOrAgentFromProxy ? agentOrAgentFromProxy._id : null;
      this.isAgentAssisted = customer && customer.application ? customer.application.isAgentAssistant : false;
    }

    if (changes) {
      Object.keys(changes).forEach((entityName) => {
        if (changes[entityName]) {
          changes[entityName].forEach((entity) => {
            if (entity.properties && entity.properties.length) {
              // only create AuditEntity row if entity has properties that were changed
              this.entities.push(AuditEntity.new(entity, this));
            }
          });
        }
      });
    }

    return this;
  }

  private constructor() {
    super();
  }

  @property({ comment: 'Event that is triggering updates to the database.' })
  functionName: string;

  @property({ comment: 'Id of the logged in user' })
  userId: string;

  @property({ comment: 'Id for the agent on behalf of whom proxy is sending quote/application' })
  agentId: string;

  @property({ comment: 'Application on which agent is working on to send it as agent assisted app' })
  applicationId: string;

  @property({ comment: 'Application on which agent is working on to send it as agent assisted app' })
  customerId: string;

  @property({ comment: 'If this app is agent assisted' })
  isAgentAssisted: boolean;

  @property({ type: Array, getType: () => AuditEntity })
  entities: Array<AuditEntity> = [];

  async save(txn): Promise<Array<Array<DbTransaction>>> {
    return Promise.all([this.persistSave(txn), this.entities.map((entity) => entity.save(txn))]);
  }
}
